/**
 * Design Grammar Engine — the composition rules of a professional flyer.
 *
 * The grammar is an ordered set of productions:
 *
 *   Background → Hero → Primary Heading → Secondary Heading
 *              → Offer → CTA → Footer
 *
 * Each node declares the zone it lives in, whether it is required, and which
 * command family expresses it. expandGrammar() takes the content the caller
 * (later: OpenAI) wants and returns the ordered node list the composition
 * engine must emit — nothing more, nothing less.
 */

export const GRAMMAR_NODES = [
  { type: 'background', zone: null, required: true, command: 'createCanvas', layer: 0 },
  { type: 'hero', zone: 'hero', required: true, command: 'createHero', layer: 1 },
  { type: 'primaryHeading', zone: 'hero', required: true, command: 'createHeadline', layer: 3 },
  { type: 'secondaryHeading', zone: 'hero', required: false, command: 'createSubheadline', layer: 3 },
  { type: 'body', zone: 'content', required: false, command: 'createParagraph', layer: 3 },
  { type: 'offer', zone: 'offer', required: true, command: 'createBadge', layer: 3 },
  { type: 'cta', zone: 'offer', required: true, command: 'createCTA', layer: 4 },
  { type: 'footer', zone: 'footer', required: true, command: 'createFooter', layer: 1 },
];

const NODE_ORDER = GRAMMAR_NODES.map((n) => n.type);

export const getGrammarNode = (type) => GRAMMAR_NODES.find((n) => n.type === type) || null;

/**
 * Expand requested content into an ordered, validated node sequence.
 * `content` maps node types to their payload, e.g.
 *   { primaryHeading: { text: 'Summer Sale' }, cta: { text: 'Shop Now' } }
 * Required nodes missing from `content` are included with empty payloads so
 * the validation engine can flag them instead of silently dropping them.
 */
export const expandGrammar = (content = {}) => {
  const unknown = Object.keys(content).filter((t) => !NODE_ORDER.includes(t));
  if (unknown.length) {
    throw new Error(`Unknown grammar node(s): ${unknown.join(', ')}`);
  }
  return GRAMMAR_NODES
    .filter((node) => node.required || content[node.type] !== undefined)
    .map((node) => ({
      ...node,
      payload: content[node.type] || {},
    }));
};

/**
 * Check that a sequence of node types respects grammar order and includes
 * all required nodes. Returns { valid, errors[] }.
 */
export const validateSequence = (types) => {
  const errors = [];
  GRAMMAR_NODES.filter((n) => n.required).forEach((n) => {
    if (!types.includes(n.type)) errors.push(`missing required node "${n.type}"`);
  });
  const indices = types
    .filter((t) => NODE_ORDER.includes(t))
    .map((t) => NODE_ORDER.indexOf(t));
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] < indices[i - 1]) {
      errors.push(
        `node "${types[i]}" appears before "${types[i - 1]}" — grammar order is ${NODE_ORDER.join(' → ')}`
      );
      break;
    }
  }
  return { valid: errors.length === 0, errors };
};
